import style from "./commandBlock.module.css";
import { useState, useRef, useEffect, useContext } from "react";

import CodeBox from "./components/CodeBox/CodeBox.tsx";
import { Square, SquareCheckBig, Trash2 } from "lucide-react";
import { apiGetCommandByID, apiGetRequestByID } from "../../../../../../../../../../sdk.ts";
import { AuthContext } from "../../../../../../../../../../contexts/AuthContext.ts";
import Tooltip from "../../../../../../../../../Common/Tooltips/Tooltip/Tooltip.tsx";
import { IOBlock } from "../../../../../../../../../../sdkStructs.ts";

interface Props {
  data: IOBlock;

  selected: boolean;
  selectFun: (data: IOBlock) => void;

  reportMode: boolean;
  standalone: boolean;
}

function msToTime(timestamp: number) {
  const date = new Date(timestamp * 1000);
  const hours = date.getHours();

  const minutes = date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes();

  return hours + ":" + minutes;
}

export function CommandBlock({ data, selectFun, selected, standalone }: Readonly<Props>) {
  const [extendedOutput, setExtendedOutput] = useState("");
  const token = useContext(AuthContext).token;

  // Determine highlight language
  const [syntaxLanguage, setSyntaxLanguage] = useState("text");
  useEffect(() => {
    if (data.type === "command") setSyntaxLanguage("bash");
    if (data.type === "request") setSyntaxLanguage("http");
  }, [data.type]);

  // Determine whether to show shadow effect
  const [extended, setExtended] = useState(false);
  const [overflowY, setOverflowY] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getShadowEffect();
  }, [wrapper.current?.scrollHeight]);

  function getShadowEffect() {
    const element = wrapper.current;
    if (!element) {
      return;
    }

    if (element.scrollHeight > element.clientHeight) setOverflowY(true);
    else setOverflowY(false);
  }

  // Choose classes based on status
  const getExtendedStatus = extended ? style.sectionFull : style.sectionSmall;
  const getExtendedWrapperStatus = !overflowY || extended ? "" : style.showGradient;

  const getSelectedStatus = selected ? style.sectionSelected : style.sectionNormal;
  const tagColor = selected ? "var(--light-blue-icon)" : "white";

  async function isClicked() {
    // Do not close the modal if there is text selected inside
    if (!extended) {
      setExtended(true);

      const response =
        data.type === "command" ? await apiGetCommandByID(token, data.id) : await apiGetRequestByID(token, data.id);
      const brute_response = JSON.parse(JSON.stringify(response));
      const field = data.type === "command" ? brute_response.data.Output : brute_response.data.Response;
      setExtendedOutput(field);
      return;
    }

    if (window.getSelection()?.type == "Range") return;
    setExtendedOutput("");
    setExtended(false);
  }
  const startBookmarking = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    selectFun(data);
  };

  function getIcon() {
    if (!standalone)
      return (
        <div className={style.trashButtonWrapper}>
          <Tooltip
            trigger={
              <button className={style.commandBlockButton + " " + style.trashButton} onClick={() => selectFun(data)}>
                <Trash2 color="var(--light-blue-icon)" strokeWidth={2} />
              </button>
            }
            description={"Remove from vuln"}
            showArrow
          />
        </div>
      );

    return (
      <div className={style.bookmarkButtonWrapper}>
        <Tooltip
          trigger={
            <button className={style.commandBlockButton + " " + style.bookmarkButton} onClick={startBookmarking}>
              {selected ? (
                <SquareCheckBig color={tagColor} strokeWidth={2} />
              ) : (
                <Square color={tagColor} strokeWidth={2} />
              )}
            </button>
          }
          description={"Tag"}
          showArrow
        />
      </div>
    );
  }

  function getOptions() {
    return (
      <div className={style.optionsWrapper}>
        <span className={style.timestampFloat}>{msToTime(data.timestamp)}</span>
        {getIcon()}
      </div>
    );
  }

  return (
    <div className={style.commandBlockWrapper}>
      {getOptions()}

      <div
        ref={wrapper}
        className={
          style.commandWrapper + " " + getSelectedStatus + " " + getExtendedWrapperStatus + " " + getExtendedStatus
        }
        onClick={isClicked}

      >
        <div className={style.section + " " + style.sectionFull}>
          <CodeBox code={data.input} separator={true} language={syntaxLanguage} />
        </div>

        <div className={style.section + " " + style.sectionFull}>
          <CodeBox
            code={extended && extendedOutput ? extendedOutput : data.output}
            separator={false}
            language={syntaxLanguage}
          />
        </div>
      </div>
    </div>
  );
}

export default CommandBlock;
